import { useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { FaInstagram, FaFacebookF, FaLinkedinIn } from "react-icons/fa";
import { HOTSPOTS, SOCIALS } from "@/data/content";
import { PillButton } from "@/components/PillButton";
import { SectionLabel } from "@/components/SectionLabel";
import { Reveal } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const PLACE = {
  right: "left-8 top-1/2 -translate-y-1/2",
  left: "right-8 top-1/2 -translate-y-1/2",
  top: "bottom-8 left-1/2 -translate-x-1/2",
  bottom: "top-8 left-1/2 -translate-x-1/2",
};

const Hotspot = ({ data, index }) => {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const show = hovered || pinned;
  return (
    <div className="absolute" style={{ left: data.x, top: data.y }}>
      <div className="relative">
        <button
          data-testid={`hero-hotspot-${index + 1}`}
          aria-label={data.title}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={() => setPinned((p) => !p)}
          className="relative grid place-items-center h-6 w-6 focus-ring rounded-full"
        >
          <span className="absolute h-6 w-6 rounded-full border border-white/70 anim-pulsering" />
          <span className="h-2.5 w-2.5 rounded-full bg-white shadow-[0_0_12px_rgba(255,255,255,0.85)]" />
        </button>
        <AnimatePresence>
          {show && (
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.18 }}
              className={`absolute z-20 w-[220px] rounded-2xl border border-[var(--border-subtle)] bg-[rgba(22,22,22,0.85)] backdrop-blur-xl p-4 shadow-[var(--shadow-md)] ${PLACE[data.place]}`}
            >
              <div className="font-playfair text-[var(--accent-secondary)] text-base">{data.title}</div>
              <div className="mt-1 text-[13px] leading-relaxed text-[var(--text-secondary)]">{data.desc}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export const Hero = () => {
  const { scrollY } = useScroll();
  const yBrand = useTransform(scrollY, [0, 700], [0, 150]);
  const yContent = useTransform(scrollY, [0, 700], [0, 70]);
  const opacity = useTransform(scrollY, [0, 520], [1, 0]);

  return (
    <section id="home" data-testid="hero" className="relative min-h-[100svh] w-full overflow-hidden">
      <div className="absolute inset-0">
        <img src="/images/hero.jpg" alt="Luxury residential tower by Godavari Builders at golden hour in Nashik" className="h-full w-full object-cover anim-kenburns" />
        <div className="absolute inset-0" style={{ background: "var(--hero-overlay)" }} />
        <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-10%,transparent_45%,rgba(12,12,12,0.55))]" />
      </div>

      <motion.div style={{ y: yBrand }} className="pointer-events-none absolute inset-x-0 top-[20%] md:top-[18%] flex justify-center select-none">
        <span
          className="font-playfair font-extrabold uppercase leading-none"
          style={{ fontSize: "clamp(60px,15vw,230px)", color: "rgba(245,230,208,0.05)", WebkitTextStroke: "1px rgba(245,230,208,0.10)" }}
        >
          GODAVARI
        </span>
      </motion.div>

      <div className="absolute inset-0 hidden sm:block">
        {HOTSPOTS.map((h, i) => (
          <Hotspot key={i} data={h} index={i} />
        ))}
      </div>

      <motion.div style={{ y: yContent, opacity }} className="relative z-10 container-pad flex min-h-[100svh] flex-col justify-end pb-24 md:pb-28">
        <div className="max-w-[760px]">
          <Reveal><SectionLabel>SINCE 1999</SectionLabel></Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-5 font-playfair font-extrabold text-[var(--text-primary)] leading-[0.98]" style={{ fontSize: "clamp(40px,7vw,86px)" }}>
              Building Dreams,<br />Crafting <em className="accent-italic">Legacies.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 max-w-[52ch] text-[var(--text-secondary)] text-base md:text-lg">
              Premium 2 &amp; 3 BHK luxury flats and row houses in Nashik &mdash; crafted with timeless elegance since 1999.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <PillButton data-testid="hero-primary-cta-button" onClick={() => scrollToId("properties")}>Explore Properties</PillButton>
              <PillButton data-testid="hero-secondary-cta-button" variant="outline" onClick={() => scrollToId("contact")}>Book a Visit</PillButton>
            </div>
          </Reveal>
        </div>
      </motion.div>

      <div className="absolute bottom-10 right-5 md:right-10 z-10 hidden sm:flex flex-col gap-3">
        {[{ I: FaInstagram, h: SOCIALS.instagram }, { I: FaLinkedinIn, h: SOCIALS.linkedinCompany }, { I: FaFacebookF, h: SOCIALS.facebook }].map(({ I, h }, i) => (
          <a key={i} href={h} target="_blank" rel="noopener noreferrer" aria-label="Social link" className="h-10 w-10 grid place-items-center rounded-full border border-[var(--border-subtle)] bg-[rgba(12,12,12,0.4)] backdrop-blur text-[var(--text-secondary)] hover:text-[var(--accent-secondary)] hover:border-[var(--border-hover)] transition-colors">
            <I size={15} />
          </a>
        ))}
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <span className="text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)]">Scroll</span>
        <span className="relative block h-10 w-[1.5px] overflow-hidden bg-[var(--border-hover)]">
          <span className="absolute top-0 left-0 h-3 w-full bg-[var(--accent-primary)] anim-scrolldot" />
        </span>
      </div>
    </section>
  );
};
